import { useNavigate } from 'react-router-dom';
import { estimateTotal, formatWeekRange, messages } from '@fruitways/shared';
import { Button, EmptyState, Icon, SkeletonRows } from '@/components';
import { AppFrame } from '@/app/AppFrame';
import { ProfileMenu } from '@/app/ProfileMenu';
import {
  allFarmsSubmitted,
  groupByFarm,
  openWeek,
  seasonForToday,
  visibleWeekStarts,
  useEstimates,
  useMasterData,
} from '@/hooks/useMasterData';
import { useAdminFilters } from '@/app/AdminFilters';
import { useGrowers, useIsAdmin } from '@/hooks/useAdmin';
import { AdminEstimates } from './AdminEstimates';
import { EstimateRow } from './EstimateRow';

export function DashboardScreen() {
  const nav = useNavigate();
  const master = useMasterData();
  const estimates = useEstimates();
  const season = seasonForToday(master);
  const isAdmin = useIsAdmin();
  const growers = useGrowers();
  const filters = useAdminFilters();

  // Both roles follow the submission cycle: capture files against the open week whoever starts
  // it, so the card has to advertise that same week.
  const week = openWeek();

  const loading = master === undefined || estimates === undefined;
  // An admin sees every farm, so "submitted this week" follows the farm chosen in the filter.
  const submittedThisWeek = isAdmin
    ? filters.farmId
      ? estimates?.find((e) => e.weekStart === week.weekStart && e.farmId === filters.farmId)
      : undefined
    : estimates?.find((e) => e.weekStart === week.weekStart);
  /*
   * Submitting closes only once every farm the grower is assigned has an estimate in for the
   * open week; with one farm still outstanding the button stays live for it. The next Thursday
   * opens the following week. Editing stays available from the estimate's own screen until the
   * Wednesday cut-off.
   */
  const allIn =
    !isAdmin &&
    master !== undefined &&
    estimates !== undefined &&
    allFarmsSubmitted(master.farms, estimates, week.weekStart);
  // Growers see the open week and the one before it; admins keep the plain latest-two view.
  const weeks = visibleWeekStarts();
  /* How far through the grower is: none of their farms in, some of them, or all. */
  const progress = allIn
    ? messages.dashboard.all
    : submittedThisWeek
      ? messages.dashboard.some
      : messages.dashboard.none;
  const groups =
    master && estimates
      ? groupByFarm(
          isAdmin ? estimates : estimates.filter((e) => weeks.includes(e.weekStart)),
          master.farms,
          2,
        )
      : [];
  const farmName = (id: string) => master?.farms.find((f) => f.id === id)?.name ?? '';

  return (
    <AppFrame
      title={
        <img
          src="/fruitways-logo.png"
          alt="Fruitways"
          style={{ height: '34px', width: 'auto', display: 'block' }}
        />
      }
      action={<ProfileMenu />}
    >
      <div className="fw-content">
        <div className="fw-panel fw-panel--tint">
          <Icon
            name="calendar-outline"
            size={24}
            color="var(--color-primary)"
            style={{ position: 'absolute', top: '16px', right: '16px' }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span className="fw-title">{messages.common.weekN(week.weekNumber)}</span>
            <span className="fw-secondary fw-tnum">
              {season
                ? messages.common.weekDatesSeason(formatWeekRange(week.weekStart), season.label)
                : formatWeekRange(week.weekStart)}
            </span>
          </div>
          {isAdmin ? (
            <span style={{ font: 'var(--type-body)' }}>
              {submittedThisWeek
                ? messages.dashboard.submittedBody(
                    estimateTotal(submittedThisWeek),
                    farmName(submittedThisWeek.farmId),
                  )
                : messages.dashboard.dueBody}
            </span>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span className="fw-body-medium">{progress.title}</span>
              <span style={{ font: 'var(--type-body)' }}>{progress.body}</span>
            </div>
          )}
          <Button onClick={() => nav('/capture')} disabled={allIn}>
            {messages.dashboard.capture}
          </Button>
        </div>

        {isAdmin && (
          <AdminEstimates master={master} estimates={estimates} growers={growers} mode="recent" />
        )}
        {!isAdmin && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <span className="fw-label">{messages.dashboard.recent}</span>
            {loading && <SkeletonRows count={3} />}
            {!loading && groups.length === 0 && (
              <EmptyState icon="leaf-outline" title={messages.dashboard.emptyTitle}>
                {messages.dashboard.emptyBody}
              </EmptyState>
            )}
            {groups.map((g) => (
              <div key={g.farm.id} className="fw-group">
                <span className="fw-label fw-label--primary">{g.farm.name.toUpperCase()}</span>
                <div className="fw-rows">
                  {g.rows.map((e) => (
                    <EstimateRow key={e.id} estimate={e} showVarieties={false} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppFrame>
  );
}
